import { ColumnDef } from '@tanstack/react-table'
import { TagCell } from 'components/table/cells/TagCell'
import { TextCell } from 'components/table/cells/TextCell'
import { ThumbnailLinkCell } from 'components/table/cells/ThumbnailCell'
import { Thumbnail } from 'components/table/utility/Thumbnail'
import { AssetImage } from 'features/assetDatabase'
import { PartsData } from 'features/partsDatabase/types/partsTableTypes'
import { useMemo } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import {
  InputType,
  machinedForgingStageColorMap,
  MachinedForgingStageContainer,
} from 'types'

import { convertDateToDistanceToNow } from './PartsTableHelpers'

const PartsTableColumns = (siteID: string): ColumnDef<PartsData>[] => {
  return useMemo(() => {
    const defaultColumns: ColumnDef<PartsData>[] = [
      {
        accessorKey: 'componentName',
        id: 'componentName',
        header: 'Part',
        filterFn: 'includesString',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => {
          return (
            <ThumbnailLinkCell
              data-testid={`asset-${row.original.id}`}
              thumbnailString={row.getValue('componentName')}
              to='/assets/$siteID/$unitID'
              from='/parts/$siteID'
              params={{ siteID, unitID: row.original.id }}
              search={{
                view: 'history',
              }}>
              <Thumbnail>
                <AssetImage
                  primaryImageID={row.original.unitPrimaryImage}
                  assetModelType={row.original.assetModelType}
                />
              </Thumbnail>
            </ThumbnailLinkCell>
          )
        },
      },
      {
        accessorKey: 'stage',
        id: 'stage',
        header: 'Stage',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => {
          const stage = row.getValue('stage') as string
          return (
            <MachinedForgingStageContainer
              bgColor={machinedForgingStageColorMap.get(stage)}
              textColor={CantileverTheme.colors.grayscale['700']}>
              {stage}
            </MachinedForgingStageContainer>
          )
        },
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: 'Status',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => {
          const status = row.getValue('status') as string
          return (
            <TagCell
              text={status}
              color={CantileverTheme.colors.grayscale['500']}
              textColor={CantileverTheme.colors.grayscale['100']}
            />
          )
        },
      },
      {
        accessorKey: 'lastUpdated',
        id: 'lastUpdated',
        header: 'Last Updated',
        sortingFn: 'datetime',
        sortUndefined: 'first',
        filterFn: 'dateRangeFilter',
        meta: {
          inputType: InputType.Date,
        },
        cell: ({ row }) => (
          <TextCell
            text={convertDateToDistanceToNow(row.getValue('lastUpdated'))}
          />
        ),
      },
      {
        accessorKey: 'erpId',
        id: 'erpId',
        header: 'Customer ID',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => <TextCell text={row.getValue('erpId')} />,
      },
    ]
    return defaultColumns
  }, [siteID])
}

export default PartsTableColumns
